import React, { useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { ControllerTextInput } from "components/basic/controller-inputs/ControllerInputs";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputTextarea } from "primereact/inputtextarea";
import { Controller, useForm } from "react-hook-form";
import { T } from "services/lang/LanguageService";
import { z } from "zod";
import timeout from "utils/timeout";

const schema = z.object({
  jwtToken: z
    .string({ message: T("word.required-fields") })
    .trim()
    .nonempty(),
});

type GenerateLinkDialogProps = {
  show: boolean;
  setVisible: React.Dispatch<React.SetStateAction<boolean>>;
};

const GenerateLinkDialog: React.FC<GenerateLinkDialogProps> = ({ show, setVisible }) => {
  return (
    <Dialog
      header={T("action.generate-link")}
      visible={show}
      className="sm:w-30rem w-full"
      onHide={() => setVisible(false)}
    >
      <GenerateLinkForm />
    </Dialog>
  );
};

export default GenerateLinkDialog;

const initLink = `${window.location.origin}/EASAS/headless/ui?_token={JWT_TOKEN}/${window.location.hash}`;

const GenerateLinkForm: React.FC = () => {
  const [link, setLink] = useState(initLink);
  const [copied, setCopied] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<{ jwtToken: string }>({
    resolver: zodResolver(schema),
  });

  const handleCopy = (url: string = link) => {
    navigator.clipboard.writeText(url);
    setCopied(true);
    timeout(() => setCopied(false), 2000);
  };

  const onSubmit = (data: { jwtToken: string }) => {
    const newLink = initLink.replace("{JWT_TOKEN}", data.jwtToken);
    setLink(newLink);
    handleCopy(newLink);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-column align-items-end">
      <Controller
        name="jwtToken"
        control={control}
        render={({ field }) => (
          <ControllerTextInput field={field} errors={errors} label={T("word.jwt-token")} col={12} />
        )}
      />
      <div className="px-2 mb-4 w-full relative">
        <InputTextarea value={link} readOnly className="w-full" rows={10} />
        <i
          role="button"
          tabIndex={0}
          onClick={() => handleCopy()}
          className={`pi ${copied ? "pi-check" : "pi-copy"} transition-all transition-duration-300 cursor-pointer p-2 m-3 border-round text-white-alpha-70 absolute right-0 top-0 ${copied ? "bg-green-800 opacity-80" : "bg-black-alpha-50 hover:bg-black-alpha-80"}`}
        />
      </div>
      <Button label={T("action.generate")} outlined type="submit" />
    </form>
  );
};
